import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, Mail, Phone, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtDateTime } from "../../lib/api";
import { PageHeader, LoadingRows } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

function ChannelCard({ icon: Icon, title, target, verified, required, providerOk, providerNote, onSend, onConfirm, busy, testid }) {
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  return (
    <div className="bg-card border border-border rounded-2xl p-6 space-y-4" data-testid={`verify-${testid}`}>
      <div className="flex items-center justify-between gap-3">
        <p className="font-display font-bold text-sm flex items-center gap-2"><Icon className="h-4 w-4" /> {title}</p>
        {verified
          ? <span className="text-xs font-bold text-emerald-500 flex items-center gap-1" data-testid={`verify-${testid}-done`}><CheckCircle2 className="h-4 w-4" /> Verified</span>
          : required
            ? <span className="text-xs font-bold text-amber-500">Required</span>
            : <span className="text-xs font-bold text-muted-foreground">Optional</span>}
      </div>
      <p className="text-xs text-muted-foreground truncate">{target || "Not set"}</p>
      {!verified && required && !providerOk && (
        <p className="text-xs font-semibold text-red-500" data-testid={`verify-${testid}-noprovider`}>{providerNote}</p>
      )}
      {!verified && (
        <div className="space-y-3">
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={busy || (required && !providerOk)} data-testid={`verify-${testid}-send`}
              onClick={async () => { const ok = await onSend(); if (ok) setSent(true); }} className="rounded-full font-bold">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCw className="h-3.5 w-3.5 mr-1" />{sent ? "Resend Code" : "Send Code"}</>}
            </Button>
          </div>
          {sent && (
            <div className="flex gap-2 items-end">
              <div className="space-y-1.5 flex-1">
                <Label>6-digit code</Label>
                <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputMode="numeric" placeholder="••••••" className="h-11 rounded-xl tracking-[0.4em] font-mono text-center"
                  data-testid={`verify-${testid}-code`} />
              </div>
              <Button disabled={busy || code.length !== 6} onClick={() => onConfirm(code)} data-testid={`verify-${testid}-confirm`}
                className="rounded-full font-bold gradient-bg text-white border-0 h-11">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function CreatorVerify() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get("/vid/creator/verify/status").then((r) => setSt(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  if (!st) return <LoadingRows rows={3} />;

  const send = (channel) => async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/vid/creator/verify/send", { channel });
      if (data.dev_code) toast.info(`DEV mode — code: ${data.dev_code}`, { duration: 30000 });
      else toast.success(data.message);
      return true;
    } catch (e) { toast.error(errMsg(e)); return false; } finally { setBusy(false); }
  };

  const confirm = (channel) => async (code) => {
    setBusy(true);
    try {
      const { data } = await api.post("/vid/creator/verify/confirm", { channel, code });
      toast.success(data.verified ? "Account fully verified — all features unlocked!" : `${channel === "email" ? "Email" : "Mobile"} verified`);
      await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-8 max-w-2xl" data-testid="creator-verify-page">
      <PageHeader title="Verify Account" subtitle="Email verification unlocks referral rewards and withdrawals" testid="verify-title" />

      {st.verified ? (
        <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-6 text-center space-y-3" data-testid="verify-complete">
          <ShieldCheck className="h-10 w-10 text-emerald-500 mx-auto" />
          <p className="font-display font-extrabold text-lg">Your creator account is fully verified</p>
          <Button onClick={() => navigate("/creator")} data-testid="verify-goto-dash" className="rounded-full font-bold gradient-bg text-white border-0 px-8">Go to Dashboard</Button>
        </div>
      ) : (
        <>
          {st.deadline && (
            <p className={`text-sm font-semibold ${st.deadline_passed ? "text-red-500" : "text-amber-500"}`} data-testid="verify-deadline">
              {st.deadline_passed ? "Deadline passed — verify now to restore referral rewards & withdrawals."
                : `Verification deadline: ${fmtDateTime(st.deadline)}`}
            </p>
          )}
          <ChannelCard icon={Mail} title="Email Verification" target={st.email} verified={st.email_verified}
            required={st.require_email} providerOk={st.providers.email}
            providerNote="Email provider is not configured on the server — ask the platform admin to configure EMERGENT_EMAIL_KEY."
            onSend={send("email")} onConfirm={confirm("email")} busy={busy} testid="email" />
          <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="verify-mobile-note">
            <Phone className="h-4 w-4" /> Mobile number is optional — used only for account contact. It is not required for verification.
          </p>
        </>
      )}
    </div>
  );
}
